import {ButtonContainer, IDataTableColumn, useGridState, useSearchApi} from "nsitools-react";
import * as React from "react";
import {useHistory} from "react-router";

import {DrupalRequestSearch, ERequestType, RequestApi, RequestGridDto} from "../../../../api";
import {AddButton, EditButton} from "../../../../components";
import {useAuth} from "../../../../contexts";
import {useApiService, useTl} from "../../../../hooks";
import {ETLCodes} from "../../../../locales";
import {getDrupalRouteStr} from "../../../../utils/RequestRouteUtil";
import {DrupalDataTable} from "../../../common";
import {DrupalFieldSet} from "../../../common/DurpalFieldSet";
import {EDrupalRoutes} from "../../../EDrupalRoutes";
import {isBrcs} from "../../../DrupalApp";

export interface IDrupalRequestListProps {
    finishedOnly: boolean;
}

export const DrupalRequestList: React.FunctionComponent<IDrupalRequestListProps> = ({finishedOnly}) => {
    const api = useApiService(RequestApi);
    const {t} = useTl();
    const {user} = useAuth();
    const history = useHistory();
    const redirectToRequest = React.useCallback(
        async (requestId: number, requestType: ERequestType) => {
            if (requestType !== null) {
                const subRequestId = await api.getSubRequestTypeId(requestId, requestType);
                history.push(`${getDrupalRouteStr(requestType)}/${subRequestId}`);
            } else {
                history.push(`${EDrupalRoutes.newRequest}`);
            }
            return;
        },
        [api, history]
    );

    const tableState = useGridState<any>({
        serverMode: true,
        enablePagination: true,
        enableFilter: true,
        sortKeys: {requestDate: "DESC"},
        availablePageSizes: [5, 10, 15],
        pageSize: finishedOnly ? 5 : 10,
    });
    const {totalCount} = tableState;
    const searchFunction = React.useCallback(
        (sObj?) => {
            const sobj = DrupalRequestSearch.fromJS({
                ...sObj,
                userId: user.userId,
                active: !finishedOnly,
                brcs: isBrcs
            });
            return api.drupalSearch(sobj);
        },
        [api, finishedOnly, user.userId]
    );

    const {loading} = useSearchApi({
        searchFunction,
        tableState,
        initialSearch: true,
        trimSearchObjectStringValues: true,
    });

    const columns = React.useMemo<IDataTableColumn[]>(
        () => [
            {
                computed: true,
                disableSorting: true,
                fieldName: "actions",
                autoFitContent: true,
                render: (row: RequestGridDto) => (
                    <ButtonContainer>
                        <EditButton
                            minimal={true}
                            onClick={async () => await redirectToRequest(row.requestId, row.requestTypeCode)}
                            secured={false}
                        />
                    </ButtonContainer>
                ),
            },
            {
                header: () => t(ETLCodes.RequestType),
                fieldName: "requestType",
            },
            {
                header: () => t(ETLCodes.RequestNumber),
                fieldName: "requestNumber",
            },
            {
                header: () => t(ETLCodes.RequestDate),
                fieldName: "requestDate",
            },
            {
                header: () => t(ETLCodes.RequestState),
                fieldName: "requestState",
            },
        ],
        [redirectToRequest, t]
    );

    return (
        <DrupalFieldSet
            title={`${finishedOnly ? t(ETLCodes.FinishedRequests) : t(ETLCodes.CurrentRequests)} (${totalCount})`}
            rightElement={
                !finishedOnly && (
                    <AddButton
                        secured={false}
                        text={t(ETLCodes.DrupalCreateNewRequest)}
                        onClick={() => redirectToRequest(0, null)}
                    />
                )
            }
        >
            <DrupalDataTable tableState={tableState} loading={loading} columns={columns}/>
        </DrupalFieldSet>
    );
};
